/**
 * The set-password screens' view of the password policy.
 *
 * THE RULES ARE NOT HERE. They live in `packages/shared/src/password-policy.ts`,
 * which the API imports to ENFORCE them. This module only adapts that verdict
 * into what the UI renders: a four-state bar and one line of copy.
 *
 * That split is the point. A second copy of "what counts as acceptable" on the
 * client would drift from the server's copy, and the failure is silent and
 * user-hostile in both directions — either the form accepts a password the API
 * then rejects, or it blocks one the API would have taken. The one thing this
 * file must never grow is a rule of its own.
 */
import { checkPassword as checkPasswordShared, passwordStrength } from '@coffman-tm/shared';

export { PASSWORD_MIN_LENGTH } from '@coffman-tm/shared';
export type { PasswordProblem } from '@coffman-tm/shared';

/**
 * What the 4px bar shows. `none` is "nothing typed yet" — a bar at zero rather
 * than a red bar, because an empty field has not failed anything. The shared
 * `passwordStrength` cannot express that distinction (it returns 0 for both
 * empty and invalid), which is the one thing this adapter adds.
 */
export type PasswordStrength = 'none' | 'weak' | 'fair' | 'strong';

export interface PasswordVerdict {
  /** True only when the SHARED policy accepts it. Never a local judgement. */
  ok: boolean;
  /** Copy to render under the field. Null while the field is untouched. */
  message: string | null;
  strength: PasswordStrength;
}

/**
 * @param password what the user typed
 * @param email the account's email, when the page knows it. Accept-invite and
 *   reset links may not carry one — the pages fire nothing on mount, so they
 *   only know what the URL told them. The shared policy skips the local-part
 *   check when it is absent, and the server still applies its own copy.
 */
export function checkPassword(password: string, email = ''): PasswordVerdict {
  if (password.length === 0) {
    return { ok: false, message: null, strength: 'none' };
  }

  const verdict = checkPasswordShared(password, email || undefined);
  if (!verdict.ok) {
    return { ok: false, message: verdict.message, strength: 'weak' };
  }

  // Valid. The bar is advice from here on — `fair` is a perfectly good password
  // and the submit button does not care which of the two it is.
  return {
    ok: true,
    message: null,
    strength: passwordStrength(password) === 3 ? 'strong' : 'fair',
  };
}
